import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { toast } from '@livable/saas-react-components';
import { useState } from 'react';
import { useCurrentSubscriptionTier } from 'src/components/SubscriptionTier';
import { apPushNeedsPlan } from 'src/components/SubscriptionTier/tiers';
import { APPFOLIO_PUSH_ATTEMPT_STATUS, APPFOLIO_PUSH_REFUSAL_CODES, APPFOLIO_PUSH_STATUS } from 'src/pages/NewBillingModule/ApplyBillingFull/AppfolioPush';
import { useStore } from 'src/stores';
import { getAppfolioPushRowState } from './appfolioPushRow';

/**
 * The states a document is past being queued in. A row reporting one of these is later news than
 * anything a trigger recorded, so it wins. `notPushed` is deliberately absent.
 */
const SETTLED_AP_PUSH_STATUSES = [APPFOLIO_PUSH_STATUS.succeeded, APPFOLIO_PUSH_STATUS.failed, APPFOLIO_PUSH_STATUS.needsReconciliation];

/**
 * AP push for the bills table. Each row's state comes from the row itself — the bills list carries the
 * push verdict off the same ledger the status endpoint reads — so the table asks nothing on its own.
 *
 * The trigger is the only call made here, and it answers `pending` for every push it accepts: being
 * queued is the whole of what it can report. What that answer is worth is therefore not the answer but
 * what is left watching afterwards, and this records it so the details view's poll has something to
 * watch and the control has a reason to stay inert.
 */
export const useBillsAppfolioPush = _ref => {
  let isEnabled = _ref.isEnabled,
    onPushed = _ref.onPushed;
  const store = useStore();
  const _useState = useState({}),
    _useState2 = _slicedToArray(_useState, 2),
    refusalByBillId = _useState2[0],
    setRefusalByBillId = _useState2[1];
  // Every row is the company on screen's, so its plan answers for all of them (LB-6188).
  const needsPlan = apPushNeedsPlan(useCurrentSubscriptionTier());
  const apPushOnSetups = !!store.account.featureFlagsStore.getIsBillingOptionsEnabled;

  /**
   * One push at a time for the whole page. A second trigger would cancel the first request in flight and
   * leave that row saying nothing happened, when the service may well have queued it.
   */
  const isPushing = store.appfolioPush.pushBillAsync.inProgress;

  /**
   * `invoiceNumber` is passed only by a surface that had somewhere for a person to enter one — the
   * details view. From the table there is no field, so the bill pushes under the number it already has,
   * which is the only case the row's own guard lets through.
   */
  const handlePush = async (billId, invoiceNumber) => {
    var _resp$response$attemp, _resp$response;
    if (!billId || isPushing) return;
    setRefusalByBillId(prev => _objectSpread(_objectSpread({}, prev), {}, {
      [billId]: null
    }));
    const resp = await store.appfolioPush.pushBillAsync.run({
      billId,
      invoiceNumber
    });

    // Whatever was read about this bill before the trigger is now behind the times — a status read
    // taken a moment ago still says the document was never pushed.
    store.appfolioPush.forget(billId);
    if (!(resp !== null && resp !== void 0 && resp.isSuccessful)) {
      var _store$appfolioPush$p, _store$appfolioPush$p2;
      // The async model has already raised the server's message; the code drives what the row shows.
      const refusalCode = (_store$appfolioPush$p = (_store$appfolioPush$p2 = store.appfolioPush.pushBillAsync.error) === null || _store$appfolioPush$p2 === void 0 ? void 0 : _store$appfolioPush$p2.errorCode) !== null && _store$appfolioPush$p !== void 0 ? _store$appfolioPush$p : null;
      setRefusalByBillId(prev => _objectSpread(_objectSpread({}, prev), {}, {
        [billId]: refusalCode
      }));

      // The refusal carries no date, but the list does — and it also settles the button state.
      if (refusalCode === APPFOLIO_PUSH_REFUSAL_CODES.billAlreadyPushed) onPushed();
      return;
    }

    // A trigger either records exactly one attempt or refuses with a 422. A blocked one never left
    // for AppFolio, so it is already its own final answer and its blockers say why.
    const attempt = (_resp$response$attemp = (_resp$response = resp.response) === null || _resp$response === void 0 || (_resp$response = _resp$response.attempts) === null || _resp$response === void 0 ? void 0 : _resp$response[0]) !== null && _resp$response$attemp !== void 0 ? _resp$response$attemp : null;
    const isBlocked = (attempt === null || attempt === void 0 ? void 0 : attempt.status) === APPFOLIO_PUSH_ATTEMPT_STATUS.blocked;

    /*
     * Recording what the trigger answered, rather than waiting to be told it again. It is the freshest
     * account of this document that exists, it is what keeps the control inert instead of springing
     * back to life on `pending`, and it is what arms the status poll in the view the user is looking
     * at. The list is refreshed alongside for the table behind, which has no poll of its own while a
     * document is open in front of it.
     */
    store.appfolioPush.setStatus({
      billId,
      status: isBlocked ? APPFOLIO_PUSH_STATUS.failed : APPFOLIO_PUSH_STATUS.pending,
      pushedOn: null,
      pushedAmount: null,
      duplicateOfBillId: null,
      attempts: attempt ? [attempt] : []
    });
    if (isBlocked) return;
    toast('success', {
      body: ['Push requested — the status will update here shortly']
    });
    onPushed();
  };

  /**
   * `feature_disabled` means the environment does not offer the push at all, so the control goes rather
   * than sitting there disabled.
   */
  const isVisible = isEnabled && !Object.values(refusalByBillId).includes(APPFOLIO_PUSH_REFUSAL_CODES.featureDisabled);
  return {
    getRefusalCode: billId => {
      var _refusalByBillId$bill;
      return (_refusalByBillId$bill = refusalByBillId[billId]) !== null && _refusalByBillId$bill !== void 0 ? _refusalByBillId$bill : null;
    },
    /**
     * Where a row's push stands, from the row itself unless a trigger has since said otherwise.
     *
     * `apPush` comes off the list, which is refreshed after a push but does not land instantly — and in
     * the gap the row still reads as never pushed, which would re-offer a document already on its way.
     * So the trigger's own answer stands in for it, exactly as it does in the details view.
     *
     * A row past `pending` supersedes that answer, because "queued" cannot be newer than "landed": it is
     * what lets the list settle a row this side recorded but has nothing watching. `notPushed` is not one
     * of those — a document with a recorded trigger is not unpushed, however far behind the list is.
     *
     * Neither source carries attempts, so a row shows where its push stands but not why; the blockers
     * behind a failure are read by the details view.
     */
    getState: function (billId, billExtractStatus, apPush) {
      var _apPush$enrolled;
      let invoiceNumber = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : null;
      const rowStatus = apPush ? _objectSpread(_objectSpread({}, apPush), {}, {
        billId,
        attempts: []
      }) : null;
      const recorded = billId ? store.appfolioPush.getStatus(billId) : null;
      const pushStatus = rowStatus && SETTLED_AP_PUSH_STATUSES.includes(rowStatus.status) ? rowStatus : recorded !== null && recorded !== void 0 ? recorded : rowStatus;
      return getAppfolioPushRowState({
        billExtractStatus,
        hasReadStatus: !!pushStatus,
        invoiceNumber,
        // From the list row whichever status wins: only the list says whether the bill is enrolled (LB-6031).
        isEnrolled: (_apPush$enrolled = apPush === null || apPush === void 0 ? void 0 : apPush.enrolled) !== null && _apPush$enrolled !== void 0 ? _apPush$enrolled : null,
        isPushing,
        needsPlan,
        apPushOnSetups,
        pushStatus
      });
    },
    handlePush,
    isPushing,
    isVisible
  };
};